/**
 * Responsibility: say whether the scanner's text readers are answering.
 *
 * **A dependency you cannot see is a dependency you debug twice.** The
 * scanner reads a card with a local binary or with somebody's free API,
 * and all three ways of losing that - a spent quota, a rotated key, a
 * missing binary - look identical from the scan tab: a shortlist that is
 * slightly wronger than yesterday. This is where to look first.
 *
 * It sits on the Nodes tab because it answers the same question as the
 * rest of it: is the machinery behind the buttons actually running.
 *
 * **Checked on a button, not on a timer.** A free tier is rationed and a
 * health check spends the same quota a scan does, so polling this every
 * fifteen seconds like the node list would eat the allowance it reports
 * on.
 */
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import PauseCircleIcon from '@mui/icons-material/PauseCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useMutation } from '@tanstack/react-query';
import { SectionCard } from '@kit/components';
import { hubApi, type ScanEngine, type ScanEngines } from '../../api/client';

/** The three states a reader can be in, and what each one means. */
function statusOf(engine: ScanEngine) {
  if (!engine.configured) {
    return {
      icon: <PauseCircleIcon fontSize="small" />,
      colour: 'default' as const,
      label: 'off',
      // Not a fault: a cloud reader with no key is switched off. Showing
      // it as an error would teach somebody to ignore the row that
      // matters when it really breaks.
      hint: 'No key set, so the scanner does not use it.',
    };
  }
  if (!engine.reachable) {
    return {
      icon: <ErrorIcon fontSize="small" />,
      colour: 'error' as const,
      label: 'down',
      hint: 'Configured but not answering. Scans still work, and read worse.',
    };
  }
  return {
    icon: <CheckCircleIcon fontSize="small" />,
    colour: 'success' as const,
    label: 'live',
    hint: '',
  };
}

function Reader({ engine }: { engine: ScanEngine }) {
  const status = statusOf(engine);
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
    >
      <Chip size="small" icon={status.icon} color={status.colour} label={status.label} />
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {engine.name}
          </Typography>
          {engine.preferred ? (
            <Typography variant="caption" color="text.secondary">
              tried first
            </Typography>
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {engine.detail}
          {status.hint ? ` ${status.hint}` : ''}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
        {engine.latency_ms === null ? '' : `${engine.latency_ms} ms`}
      </Typography>
    </Stack>
  );
}

export function ReadersCard() {
  const [answer, setAnswer] = useState<ScanEngines | null>(null);
  const check = useMutation({
    mutationFn: hubApi.scanEngines,
    onSuccess: setAnswer,
  });

  return (
    <SectionCard
      title="Card readers"
      subtitle="What reads the words off a photographed card, and whether it is answering"
      headerExtra={
        <Button
          size="small"
          startIcon={check.isPending ? <CircularProgress size={14} /> : <RefreshIcon />}
          disabled={check.isPending}
          onClick={() => check.mutate()}
        >
          Check now
        </Button>
      }
    >
      {check.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Could not ask the hub which readers it has.
        </Alert>
      ) : null}

      {answer === null ? (
        <Typography variant="body2" color="text.secondary">
          Not checked yet. Each check costs one request of the free allowance, so it is
          asked for rather than polled.
        </Typography>
      ) : (
        <Box>
          {answer.engines.map((engine) => (
            <Reader key={engine.name} engine={engine} />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Order: {answer.chain}. The local reader is always last, so a cloud reader
            running out costs a shortlist rather than a scan - the artwork still
            identifies the card.
            {answer.last_used ? ` Last scan was read by ${answer.last_used}.` : ''}
          </Typography>
        </Box>
      )}
    </SectionCard>
  );
}
